import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Factory, Users, Package, Calendar, Download, Minus, Plus, Target, Play, Pause, Trash2, ChevronRight, ChevronDown, Upload, Printer, CalendarDays, FileSpreadsheet, FileText, ListTree, Pencil, TrendingUp as TrendUp, TrendingDown as TrendDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SupervisorVoiceNotes from "@/components/SupervisorVoiceNotes";
import BackfillButton from "@/components/BackfillButton";
import CleanseMismatchedOutputButton from "@/components/CleanseMismatchedOutputButton";
import Holidays from "@/components/Holidays";
import MonthlyLineReport from "@/components/MonthlyLineReport";
import PPMDReport from "@/components/PPMDReport";
import TrendVsYesterday from "@/components/TrendVsYesterday";
import { useSortableTable, SortableTh } from "@/components/SortableTable";
import * as XLSX from "xlsx";

// ── SO completion timing ──
// An SO that hits 100% stays in Active Production until the next 12 AM IST
// midnight, then moves to Completed SOs. `completed_at` is stamped server-side
// on the transition; rows completed before this feature (no stamp) count as
// already completed. EXCEPTION: when the completing output is BACK-DATED
// (recorded for a past date), the server stamps `completed_at` with that past
// date — the SO moves to Completed SOs immediately, no midnight wait.
function _istMidnightBoundary() {
  const now = new Date();
  const istNow = new Date(now.getTime() + 5.5 * 3600 * 1000);
  return new Date(Date.UTC(istNow.getUTCFullYear(), istNow.getUTCMonth(), istNow.getUTCDate()) - 5.5 * 3600 * 1000);
}
function isFullyProduced(o) {
  return (o.order_qty || 0) > 0 && (o.produced_qty || 0) >= (o.order_qty || 0);
}
function isCompletedForDisplay(o) {
  if (!isFullyProduced(o)) return false;
  if (!o.completed_at) return true; // completed before this feature shipped
  return new Date(o.completed_at) < _istMidnightBoundary();
}

import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function LineTracking() {
  const { user } = useAuth();
  const caps = user?.capabilities || [];
  const hasCap = (c) => (user?.role === "admin" || user?.role === "admin_addl") || caps.includes(c);
  const canTickActive = hasCap("edit_so_active_for_production");
  const canUntickActive = hasCap("uncheck_so_active_for_production");
  const canEditActivelyRunning = hasCap("edit_so_actively_running");
  const canEditTarget = hasCap("edit_so_daily_target");
  const canRecordVoice = hasCap("record_supervisor_note");
  const canViewVoice = hasCap("view_supervisor_notes");
  const canDeleteVoice = hasCap("delete_supervisor_notes");
  const canAssignLines = hasCap("assign_so_lines");
  const canDeleteOutputRow = hasCap("delete_output_row");
  // Order qty flows from the Google Sheet at first sync and is never re-synced —
  // Admin / Admin Addl correct it inline (backend reconciles completion state).
  const canEditOrderQty = user?.role === "admin" || user?.role === "admin_addl";
  const isLineSupervisor = user?.role === "line_supervisor";
  const [tab, setTab] = useState("dashboard");
  const [lines, setLines] = useState([]);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [production, setProduction] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [output, setOutput] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [selectedLine, setSelectedLine] = useState("");
  const [attForm, setAttForm] = useState({ stitchers: 0, helpers: 0, qc: 0 });
  const [outForm, setOutForm] = useState({ order_number: "", qty: 0 });
  const lineMap = useMemo(() => Object.fromEntries((lines || []).map((l) => [l.id, l.name])), [lines]);
  // Admin — output-rows viewer / deleter (for fixing corrupt entries like the
  // oscillating SO/25-26/291 case).
  const [outRowsFor, setOutRowsFor] = useState(null);   // order_number when modal is open
  const [outRowsData, setOutRowsData] = useState([]);
  const [outRowsLoading, setOutRowsLoading] = useState(false);
  const openOutputRows = async (order) => {
    if (!order) return;
    setOutRowsFor(order.order_number);
    setOutRowsLoading(true);
    try {
      const { data } = await api.get("/output", { params: { order_number: order.order_number } });
      setOutRowsData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load output rows");
    } finally {
      setOutRowsLoading(false);
    }
  };
  const closeOutputRows = () => { setOutRowsFor(null); setOutRowsData([]); };
  const deleteOutputRow = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(
      `Delete this output row?\n\n` +
      `Date: ${row.date}\n` +
      `Line: ${lineMap[row.line_id] || row.line_id}\n` +
      `Qty: ${row.qty}\n\n` +
      `The SO's produced_qty will drop by ${row.qty}. This cannot be undone.`
    )) return;
    try {
      const { data } = await api.delete(`/output/${row.id}`);
      toast.success(`Row deleted — produced_qty rolled back by ${data.sample_deducted}`);
      // Refresh both the modal list and the SO list itself
      const cur = outRowsFor;
      if (cur) {
        const { data: refreshed } = await api.get("/output", { params: { order_number: cur } });
        setOutRowsData(refreshed);
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const loadAll = async () => {
    const [lRes, oRes, sRes, pRes, aRes, oo] = await Promise.all([
      api.get("/masters/lines"), api.get("/orders"), api.get("/dashboard/summary"),
      api.get("/production_summary"), api.get("/attendance"), api.get("/output"),
    ]);
    setLines(lRes.data); setOrders(oRes.data); setSummary(sRes.data);
    setProduction(pRes.data); setAttendance(aRes.data); setOutput(oo.data);
    if (!selectedLine && lRes.data.length) setSelectedLine(lRes.data[0].id);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const currentAtt = useMemo(() => attendance.find((a) => a.date === date && a.line_id === selectedLine), [attendance, date, selectedLine]);
  // Existing output row for the exact (date, line, SO) triple currently in the form —
  // used to show the "already recorded today" banner + drive the delta-preview.
  const existingOutRow = useMemo(
    () => output.find((r) => r.date === date && r.line_id === selectedLine && r.order_number === outForm.order_number) || null,
    [output, date, selectedLine, outForm.order_number]
  );
  // When the SO is picked (or date/line change), prefill the qty field with the
  // existing row's qty so the number the operator sees IS today's current total,
  // not a stale zero that tricks them into thinking they're adding on top.
  useEffect(() => {
    if (!outForm.order_number) return;
    setOutForm((prev) => ({ ...prev, qty: existingOutRow ? Number(existingOutRow.qty || 0) : 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outForm.order_number, date, selectedLine, existingOutRow?.qty]);
  useEffect(() => {
    if (currentAtt) setAttForm({ stitchers: currentAtt.stitchers, helpers: currentAtt.helpers, qc: currentAtt.qc });
    else setAttForm({ stitchers: 0, helpers: 0, qc: 0 });
  }, [currentAtt]);

  const saveAttendance = async () => {
    if (!selectedLine) { toast.error("Select a line"); return; }
    await api.post("/attendance", { date, line_id: selectedLine, ...attForm });
    toast.success("Attendance saved"); loadAll();
  };

  const saveOutput = async () => {
    if (!selectedLine || !outForm.order_number) { toast.error("Select line and SO number"); return; }
    const order = orders.find((o) => o.order_number === outForm.order_number);
    const newQty = Number(outForm.qty);
    if (!Number.isFinite(newQty) || newQty < 0) { toast.error("Output qty must be 0 or a positive number"); return; }
    // Guard against the silent-decrease trap that caused SO/25-26/291 to oscillate.
    // If an existing row is being reduced, require an explicit confirm so operators
    // don't accidentally roll produced_qty back thinking they're "adding" pieces.
    if (existingOutRow && newQty < Number(existingOutRow.qty || 0)) {
      const diff = Number(existingOutRow.qty || 0) - newQty;
      if (!window.confirm(
        `You're about to REDUCE today's output on this line from ${existingOutRow.qty} → ${newQty}.\n\n` +
        `The SO's produced_qty will drop by ${diff}.\n\n` +
        `Continue only if this is a correction. Cancel and enter a DIFFERENT date/line if you meant to ADD ${newQty} more.`
      )) {
        return;
      }
    }
    await api.post("/output", { date, line_id: selectedLine, order_number: outForm.order_number, qty: newQty, style: order?.bbs_style || order?.style, buyer: order?.buyer });
    toast.success(existingOutRow ? "Today's output updated" : "Output saved");
    setOutForm({ order_number: "", qty: 0 });
    loadAll();
  };

  const toggleActive = async (order) => {
    const nextVal = !order.active_for_production;
    if (order.active_for_production && !canUntickActive) { toast.error("Only admin can un-tick an already-active SO"); return; }
    if (!order.active_for_production && !canTickActive) { toast.error("You don't have permission to activate this SO"); return; }
    try {
      await api.patch(`/orders/${encodeURIComponent(order.order_number)}`, { active_for_production: nextVal });
      toast.success(nextVal ? `${order.order_number} marked active for production` : `${order.order_number} deactivated`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveDailyTarget = async (order, raw) => {
    if (!canEditTarget) { toast.error("You don't have permission to set daily target"); return; }
    const trimmed = String(raw ?? "").trim();
    try {
      await api.patch(`/orders/${encodeURIComponent(order.order_number)}`, { daily_target: trimmed === "" ? null : Number(trimmed) });
      toast.success(`Daily target updated for ${order.order_number}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const toggleActivelyRunning = async (order) => {
    if (!canEditActivelyRunning) { toast.error("You don't have permission to change the 'Actively Running' flag"); return; }
    const nextVal = !order.actively_running;
    try {
      await api.patch(`/orders/${encodeURIComponent(order.order_number)}`, { actively_running: nextVal });
      toast.success(nextVal ? `${order.order_number} marked actively running — target alerts will fire from today` : `${order.order_number} 'actively running' turned off`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveLineDailyTargets = async (order, map) => {
    if (!canEditTarget) { toast.error("You don't have permission to set per-line targets"); return; }
    try {
      await api.patch(`/orders/${encodeURIComponent(order.order_number)}`, { line_daily_targets: map });
      toast.success(`Per-line targets updated for ${order.order_number}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveOrderQty = async (order, raw) => {
    if (!canEditOrderQty) { toast.error("Only Admin can change the order quantity"); return; }
    const n = Number(String(raw ?? "").replace(/,/g, "").trim());
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive order quantity"); return; }
    if (n === Number(order.order_qty || 0)) return;
    const produced = Number(order.produced_qty || 0);
    const note = produced >= n
      ? `\n\nNote: produced (${produced.toLocaleString()}) is already ≥ the new qty — the SO will move to Completed SOs.`
      : (produced >= Number(order.order_qty || 0)
        ? `\n\nNote: the new qty is above produced (${produced.toLocaleString()}) — the SO will return to Active Production.`
        : "");
    if (!window.confirm(`Change order qty for ${order.order_number} from ${Number(order.order_qty || 0).toLocaleString()} to ${n.toLocaleString()}?${note}`)) return;
    try {
      await api.patch(`/orders/${encodeURIComponent(order.order_number)}`, { order_qty: n });
      toast.success(`Order qty updated for ${order.order_number}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const toggleAssignedLine = async (order, lineId) => {
    if (!canAssignLines) { toast.error("You don't have permission to assign lines"); return; }
    const cur = order.assigned_lines || [];
    const next = cur.includes(lineId) ? cur.filter((x) => x !== lineId) : [...cur, lineId];
    try {
      await api.patch(`/orders/${encodeURIComponent(order.order_number)}`, { assigned_lines: next });
      loadAll();
    } catch (e) {
      // The backend blocks with HTTP 409 when the admin is removing a line that still has
      // output rows against this SO (mirrors the POST /output guard from the SO-side).
      // Surface the orphan summary as an actionable confirmation instead of a generic toast.
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && typeof d === "object" && Array.isArray(d.orphan_summary)) {
        const summary = d.orphan_summary
          .map((s) => `• ${s.line_name}: ${s.rows} row(s), ${s.qty} pcs`)
          .join("\n");
        const ok = window.confirm(
          `${d.message}\n\nOrphaned rows if you continue:\n${summary}\n\nProceed and LEAVE the orphaned output rows in place? (You can Cleanse them later.)\n\nRecommended: cancel and run 'Cleanse mismatched output' first.`
        );
        if (!ok) return;
        try {
          await api.patch(`/orders/${encodeURIComponent(order.order_number)}`, { assigned_lines: next, force_remove_lines: true });
          toast.warning(`Line removed. ${d.orphan_total_rows} row(s) are now orphaned — run 'Cleanse mismatched output' to purge them.`);
          loadAll();
        } catch (e2) { toast.error(e2.response?.data?.detail || "Force update failed"); }
        return;
      }
      toast.error(typeof d === "string" ? d : (d?.message || "Update failed"));
    }
  };

  const selectedOrder = orders.find((o) => o.order_number === outForm.order_number);
  const isToday = date === todayStr();

  return (
    <div className="space-y-6" data-testid="line-tracking-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><Factory className="h-3.5 w-3.5" /> Line Tracking</div>
          <h1 className="mt-1 font-heading font-bold text-2xl md:text-3xl tracking-tight">Attendance & Output</h1>
          <p className="text-slate-500 text-[13px] md:text-[14px] mt-1">Daily line-wise attendance and production entry with cumulative balance.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full sm:w-auto overflow-x-auto flex-nowrap justify-start" data-testid="line-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-lt-dashboard" className="whitespace-nowrap">Dashboard</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-lt-attendance" className="whitespace-nowrap">Attendance</TabsTrigger>
          <TabsTrigger value="output" data-testid="tab-lt-output" className="whitespace-nowrap">Output</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-lt-orders" className="whitespace-nowrap">SO List</TabsTrigger>
          {hasCap("view_holidays") && (
            <TabsTrigger value="holidays" data-testid="tab-lt-holidays" className="whitespace-nowrap">Holidays</TabsTrigger>
          )}
          {hasCap("view_holidays") && (
            <TabsTrigger value="monthly" data-testid="tab-lt-monthly" className="whitespace-nowrap">Monthly Report</TabsTrigger>
          )}
          {hasCap("view_holidays") && (
            <TabsTrigger value="ppmd" data-testid="tab-lt-ppmd" className="whitespace-nowrap">PPMD</TabsTrigger>
          )}
          <TabsTrigger value="voice" data-testid="tab-lt-voice" className="whitespace-nowrap">Voice Notes</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-lt-history" className="whitespace-nowrap">History</TabsTrigger>
        </TabsList>

        {/* ---------------- Dashboard ---------------- */}
        <TabsContent value="dashboard" className="space-y-4">
          {summary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <MiniCard label="Active Lines" value={summary.kpis.active_lines} icon={Factory} tone="brand" prev={summary.kpis_yesterday?.active_lines} prevLabel={summary.comparison?.is_yesterday === false ? summary.comparison.label : "yesterday"} trendTestid="lt-trend-active-lines" />
                <MiniCard label="Machine Operators" value={summary.kpis.stitchers} icon={Users} tone="blue" prev={summary.kpis_yesterday?.stitchers} prevLabel={summary.comparison?.is_yesterday === false ? summary.comparison.label : "yesterday"} trendTestid="lt-trend-stitchers" />
                <MiniCard label="Table Ops & Helpers" value={summary.kpis.helpers} icon={Users} tone="amber" prev={summary.kpis_yesterday?.helpers} prevLabel={summary.comparison?.is_yesterday === false ? summary.comparison.label : "yesterday"} trendTestid="lt-trend-helpers" />
                <MiniCard label="QC" value={summary.kpis.qc} icon={Users} tone="slate" prev={summary.kpis_yesterday?.qc} prevLabel={summary.comparison?.is_yesterday === false ? summary.comparison.label : "yesterday"} trendTestid="lt-trend-qc" />
                <MiniCard label="Output Today" value={summary.kpis.output_today.toLocaleString()} icon={Package} tone="emerald" prev={summary.kpis_yesterday?.output} prevLabel={summary.comparison?.is_yesterday === false ? summary.comparison.label : "yesterday"} trendTestid="lt-trend-output" />
              </div>

              <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <div className="font-heading font-semibold text-[15px]">Line-wise Production Summary</div>
                  <div className="text-[12px] text-slate-500">Today's attendance and output by line</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="opsiq-table">
                    <thead><tr><th>Line</th><th>Machine Ops</th><th>Table Ops & Helpers</th><th>QC</th><th className="text-right">Output Today</th></tr></thead>
                    <tbody>
                      {summary.line_summary.map((l) => (
                        <tr key={l.line_id}>
                          <td className="font-semibold">{l.line}</td>
                          <td><LineMetricCell today={l.stitchers} yesterday={l.stitchers_y} cmpLabel={fmtCmpLabel(l.cmp_date)} testid={`lt-line-stitchers-${l.line_id}`} /></td>
                          <td><LineMetricCell today={l.helpers} yesterday={l.helpers_y} cmpLabel={fmtCmpLabel(l.cmp_date)} testid={`lt-line-helpers-${l.line_id}`} /></td>
                          <td><LineMetricCell today={l.qc} yesterday={l.qc_y} cmpLabel={fmtCmpLabel(l.cmp_date)} testid={`lt-line-qc-${l.line_id}`} /></td>
                          <td className="text-right"><LineMetricCell today={l.output} yesterday={l.output_y} mono align="right" cmpLabel={fmtCmpLabel(l.cmp_date)} testid={`lt-line-output-${l.line_id}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {(() => {
                const completed = production.filter(isCompletedForDisplay);
                const remaining = production.filter((o) => !isCompletedForDisplay(o));
                const active = remaining.filter((o) => o.active_for_production);
                const inactive = remaining.filter((o) => !o.active_for_production);
                return (
                  <>
                    <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
                      <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-heading font-semibold text-[15px]">Active Production · SO vs Output vs Balance vs Target</div>
                          <div className="text-[12px] text-slate-500">SOs currently marked <b>Active for Production</b>. Produced totals sum across every line assigned to the SO.</div>
                        </div>
                        <span className="text-[11.5px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{active.length} active</span>
                      </div>
                      <ProdTable rows={active} lines={lines} emptyLabel="No SOs marked Active for Production yet. Head to the SO List tab to activate SOs." canEditOrderQty={canEditOrderQty} onSaveOrderQty={saveOrderQty} />
                    </Card>

                    {inactive.length > 0 && (
                      <Card className="border-slate-200 rounded-md bg-slate-50/50 overflow-hidden">
                        <details>
                          <summary className="p-4 cursor-pointer select-none flex items-center justify-between gap-3 flex-wrap hover:bg-slate-100/60">
                            <div>
                              <div className="font-heading font-semibold text-[14px] text-slate-600">Inactive SOs (not yet in production)</div>
                              <div className="text-[11.5px] text-slate-500">Click to expand — {inactive.length} SO{inactive.length === 1 ? "" : "s"} currently inactive.</div>
                            </div>
                            <span className="text-[11.5px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{inactive.length} inactive</span>
                          </summary>
                          <ProdTable rows={inactive} lines={lines} emptyLabel="—" canEditOrderQty={canEditOrderQty} onSaveOrderQty={saveOrderQty} />
                        </details>
                      </Card>
                    )}

                    {!isLineSupervisor && completed.length > 0 && (
                      <Card className="border-emerald-200 rounded-md bg-emerald-50/40 overflow-hidden" data-testid="completed-so-section">
                        <details>
                          <summary className="p-4 cursor-pointer select-none flex items-center justify-between gap-3 flex-wrap hover:bg-emerald-100/50">
                            <div>
                              <div className="font-heading font-semibold text-[14px] text-emerald-800">Completed SOs (100% produced)</div>
                              <div className="text-[11.5px] text-emerald-700/80">Click to expand — {completed.length} SO{completed.length === 1 ? "" : "s"} fully produced.</div>
                            </div>
                            <span className="text-[11.5px] font-mono px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">{completed.length} completed</span>
                          </summary>
                          <ProdTable rows={completed} lines={lines} emptyLabel="—" canEditOrderQty={canEditOrderQty} onSaveOrderQty={saveOrderQty} />
                        </details>
                      </Card>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </TabsContent>

        {/* ---------------- Attendance (mobile-first) ---------------- */}
        <TabsContent value="attendance">
          <Card className="p-4 sm:p-6 border-slate-200 rounded-md bg-white max-w-2xl mx-auto sm:mx-0" data-testid="attendance-card">
            <div className="font-heading font-semibold text-[16px] mb-1">Daily Attendance Entry</div>
            <div className="text-[12px] text-slate-500 mb-4">Head-count per line for the selected date.</div>

            <DateAndLinePicker
              date={date} setDate={setDate} isToday={isToday}
              lines={lines} selectedLine={selectedLine} setSelectedLine={setSelectedLine}
              testidPrefix="attendance"
            />

            <div className="mt-5 space-y-3">
              <StepperRow
                testid="attendance-stitchers"
                label="Machine Operators"
                value={attForm.stitchers}
                onChange={(v) => setAttForm({ ...attForm, stitchers: v })}
              />
              <StepperRow
                testid="attendance-helpers"
                label="Table Operators & Helpers"
                value={attForm.helpers}
                onChange={(v) => setAttForm({ ...attForm, helpers: v })}
              />
              <StepperRow
                testid="attendance-qc"
                label="QC"
                value={attForm.qc}
                onChange={(v) => setAttForm({ ...attForm, qc: v })}
              />
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] text-slate-500">
                {currentAtt ? "Existing entry — will update" : "New entry"}
              </div>
              <Button
                onClick={saveAttendance}
                className="bg-brand hover:bg-brand-dark text-white h-12 px-6 text-[14px] w-full sm:w-auto"
                data-testid="save-attendance-btn"
              >
                Save Attendance
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* ---------------- Output (mobile-first) ---------------- */}
        <TabsContent value="output">
          <Card className="p-4 sm:p-6 border-slate-200 rounded-md bg-white max-w-2xl mx-auto sm:mx-0" data-testid="output-card">
            <div className="font-heading font-semibold text-[16px] mb-1">Daily Output Entry</div>
            <div className="text-[12px] text-slate-500 mb-4">Pick a Sales Order and enter today's produced pieces.</div>

            <DateAndLinePicker
              date={date} setDate={setDate} isToday={isToday}
              lines={lines} selectedLine={selectedLine} setSelectedLine={setSelectedLine}
              testidPrefix="output"
            />

            <div className="mt-4">
              <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">SO Number</label>
              <Select value={outForm.order_number} onValueChange={(v) => setOutForm({ ...outForm, order_number: v })}>
                <SelectTrigger data-testid="output-order" className="mt-1 h-12"><SelectValue placeholder="Select SO" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    // SO dropdown must always be scoped to what's assigned to the selected line.
                    // Previously only line_supervisors were filtered; admin/production/merchandiser
                    // saw the full list, which let stray Line-X rows land on SOs that were
                    // actually assigned to Line-Y (production data-integrity bug). Enforcing the
                    // same rule for every role closes the loop client-side; the backend also
                    // rejects mismatches with a 400.
                    let visible = orders.filter((o) => (o.assigned_lines || []).includes(selectedLine));
                    if (isLineSupervisor) {
                      visible = visible.filter((o) => o.active_for_production);
                    }
                    if (visible.length === 0) {
                      return <div className="px-3 py-2 text-[12px] text-slate-500">
                        {isLineSupervisor
                          ? "No SOs are assigned to this line and marked active yet. Ask your admin to assign SOs to your line."
                          : "No SOs are assigned to this line yet. Open the SO's Assignment panel to assign it, or pick a different line."}
                      </div>;
                    }
                    return visible.map((o) => (
                      <SelectItem key={o.order_number} value={o.order_number}>
                        <span className="font-mono">{o.order_number}</span>
                        <span className="text-slate-500"> — {o.buyer}{o.buyer_style ? ` · ${o.buyer_style}` : ""} · {o.bbs_style || o.style}</span>
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            {selectedOrder && (
              <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-y-2 gap-x-3 text-[13px]" data-testid="output-so-summary">
                <MetaRow label="Buyer Name" value={selectedOrder.buyer || "—"} />
                <MetaRow label="Buyer's Style #" value={selectedOrder.buyer_style || "—"} />
                <MetaRow label="BBS Style No" value={selectedOrder.bbs_style || selectedOrder.style || "—"} />
                <MetaRow label="Ord Qty" value={Number(selectedOrder.order_qty || 0).toLocaleString()} mono />
              </div>
            )}

            <div className="mt-5">
              <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Today's Output (pcs)</label>
              {existingOutRow && (
                <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900" data-testid="output-existing-banner">
                  <b>Already recorded today for this line on this SO: {existingOutRow.qty} pcs.</b><br/>
                  The field below has been pre-filled with that value. Any change REPLACES today's row (it does not add on top). To add more pieces from another shift or line, pick a different date or line first.
                </div>
              )}
              <div className="mt-1 flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => setOutForm({ ...outForm, qty: Math.max(0, Number(outForm.qty) - 10) })} className="h-12 w-12 shrink-0" data-testid="output-qty-minus"><Minus className="h-4 w-4" /></Button>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  type="number"
                  min={0}
                  value={outForm.qty}
                  onChange={(e) => setOutForm({ ...outForm, qty: Number(e.target.value) })}
                  data-testid="output-qty"
                  className="h-12 text-center text-lg font-mono font-semibold"
                />
                <Button type="button" variant="outline" onClick={() => setOutForm({ ...outForm, qty: Number(outForm.qty) + 10 })} className="h-12 w-12 shrink-0" data-testid="output-qty-plus"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Enter <span className="font-mono">0</span> if no pieces were produced today — a zero-output entry is valid.</div>
            </div>

            {selectedOrder && (() => {
              const oq = Number(selectedOrder.order_qty) || 0;
              const pq = Number(selectedOrder.produced_qty) || 0;
              const existingQty = Number(existingOutRow?.qty || 0);
              const newQty = Number(outForm.qty) || 0;
              const delta = newQty - existingQty;
              const nextPq = Math.max(0, pq + delta);
              const nextBalance = Math.max(0, oq - nextPq);
              const isDecrease = delta < 0;
              const isComplete = oq > 0 && nextPq >= oq;
              return (
                <div className="mt-4 space-y-2">
                  <div className="grid grid-cols-3 gap-2 rounded-md bg-brand-soft/40 p-3">
                    <Stat label="Produced till date" value={pq} />
                    <Stat label="Balance" value={Math.max(0, oq - pq)} />
                    <Stat label="Today's Entry" value={newQty} />
                  </div>
                  <div
                    className={`rounded-md p-3 text-[12.5px] border ${
                      isComplete
                        ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                        : isDecrease
                        ? "bg-rose-50 border-rose-300 text-rose-900"
                        : "bg-slate-50 border-slate-200 text-slate-700"
                    }`}
                    data-testid="output-delta-preview"
                  >
                    <div className="font-semibold mb-0.5">After saving:</div>
                    <div>
                      Produced <b>{nextPq}/{oq || "?"}</b> · Balance <b>{nextBalance}</b> ·
                      Δ produced_qty <b>{delta > 0 ? `+${delta}` : delta}</b>
                      {isComplete && <span className="ml-1">✓ SO will complete</span>}
                      {isDecrease && <span className="ml-1">⚠ produced_qty will DECREASE</span>}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="mt-5">
              <Button onClick={saveOutput} className="bg-brand hover:bg-brand-dark text-white h-12 px-6 text-[14px] w-full sm:w-auto" data-testid="save-output-btn">
                Save Output
              </Button>
            </div>

            {/* Daily voice note — line supervisor primary user, admin/production can listen back */}
            {(canRecordVoice || canViewVoice) && (
              <div className="mt-6">
                <SupervisorVoiceNotes
                  date={date}
                  lineId={selectedLine}
                  orderNumber={outForm.order_number || null}
                  canRecord={canRecordVoice}
                  canView={canViewVoice}
                  canDelete={canDeleteVoice}
                />
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ---------------- SO list (read-only) ---------------- */}
        <TabsContent value="orders">
          <SOListTable
            orders={orders} lines={lines} user={user}
            canTickActive={canTickActive} canUntickActive={canUntickActive}
            canEditActivelyRunning={canEditActivelyRunning}
            canEditTarget={canEditTarget} canAssignLines={canAssignLines}
            canDeleteOutputRow={canDeleteOutputRow}
            openOutputRows={openOutputRows}
            toggleActive={toggleActive}
            toggleActivelyRunning={toggleActivelyRunning}
            saveDailyTarget={saveDailyTarget}
            saveLineDailyTargets={saveLineDailyTargets}
            toggleAssignedLine={toggleAssignedLine}
            loadAll={loadAll}
          />
        </TabsContent>

        {/* ---------------- Voice Notes ---------------- */}
        <TabsContent value="voice">
          <VoiceNotesBrowser lines={lines} canDelete={canDeleteVoice} />
        </TabsContent>

        {/* ---------------- Holidays ---------------- */}
        {hasCap("view_holidays") && (
          <TabsContent value="holidays">
            <Holidays canEdit={hasCap("edit_holidays")} lines={lines} />
          </TabsContent>
        )}

        {/* ---------------- Monthly Report ---------------- */}
        {hasCap("view_holidays") && (
          <TabsContent value="monthly">
            <MonthlyLineReport lines={lines} />
          </TabsContent>
        )}

        {/* ---------------- PPMD (Pieces Per Man Day) ---------------- */}
        {hasCap("view_holidays") && (
          <TabsContent value="ppmd">
            <PPMDReport lines={lines} />
          </TabsContent>
        )}



        {/* ---------------- History ---------------- */}
        <TabsContent value="history">
          <HistoryPanel lines={lines} />
        </TabsContent>
      </Tabs>

      {/* Admin — output rows viewer / deleter */}
      <Dialog open={!!outRowsFor} onOpenChange={(open) => { if (!open) closeOutputRows(); }}>
        <DialogContent className="max-w-3xl" data-testid="output-rows-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListTree className="h-5 w-5 text-brand" />Output rows for <span className="font-mono">{outRowsFor}</span></DialogTitle>
          </DialogHeader>
          <div className="text-[12.5px] text-slate-500 mb-2">
            Deleting a row will roll back the SO's <span className="font-mono">produced_qty</span> by that row's <span className="font-mono">qty</span>. Use only to fix data-entry mistakes.
          </div>
          {outRowsLoading ? (
            <div className="p-4 text-sm text-slate-500">Loading…</div>
          ) : outRowsData.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">No output rows recorded for this SO.</div>
          ) : (
            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Line</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Updated by</th>
                    <th className="text-left px-3 py-2">Updated at</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {outRowsData.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`output-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono text-slate-700">{r.date}</td>
                      <td className="px-3 py-2 text-slate-700">{lineMap[r.line_id] || r.line_id}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{Number(r.qty || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-slate-500 text-[12px]">{r.updated_by || r.created_by || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 text-[11.5px] font-mono">{(r.updated_at || r.created_at || "").replace("T", " ").replace(/\.\d+.*$/, "")}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => deleteOutputRow(r)} data-testid={`output-row-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td colSpan={2} className="px-3 py-2 text-[11.5px] font-semibold uppercase tracking-wider text-slate-600">Total</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{outRowsData.reduce((a, r) => a + Number(r.qty || 0), 0).toLocaleString()}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeOutputRows}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------- helpers -------------------- */

function DateAndLinePicker({ date, setDate, isToday, lines, selectedLine, setSelectedLine, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" /> Date {isToday && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 tracking-normal normal-case">Today</span>}
        </label>
        <div className="mt-1 flex items-center gap-2">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid={`${testidPrefix}-date`}
            className="h-12 text-[14px]"
          />
          {!isToday && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setDate(todayStr())}
              className="h-12 shrink-0 text-[12px] font-semibold"
              data-testid={`${testidPrefix}-today-btn`}
            >
              Today
            </Button>
          )}
        </div>
      </div>
      <div>
        <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Line</label>
        <Select value={selectedLine} onValueChange={setSelectedLine}>
          <SelectTrigger data-testid={`${testidPrefix}-line`} className="mt-1 h-12"><SelectValue placeholder="Select line" /></SelectTrigger>
          <SelectContent>{lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

function StepperRow({ testid, label, value, onChange }) {
  const step = 1;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-semibold text-slate-700">{label}</div>
        <div className="text-[11px] text-slate-400 font-mono">count</div>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" onClick={() => onChange(Math.max(0, Number(value) - step))} className="h-12 w-12 shrink-0" data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></Button>
        <Input
          inputMode="numeric"
          pattern="[0-9]*"
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testid}
          className="h-12 text-center text-lg font-mono font-semibold"
        />
        <Button type="button" variant="outline" onClick={() => onChange(Number(value) + step)} className="h-12 w-12 shrink-0" data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-right font-semibold ${mono ? "font-mono" : ""}`}>{value}</div>
    </>
  );
}

// Short label for the trend comparison date: "yday" when it is literally yesterday,
// otherwise the date itself (e.g. "28 Oct") so post-holiday comparisons stay honest.
function fmtCmpLabel(iso) {
  if (!iso) return "yday";
  const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (iso === yest) return "yday";
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

// Line-wise metric cell: today's value with the comparison day's figure in a smaller
// size plus a green ▲ / red ▼ / grey — arrow. `cmpLabel` shows WHICH day is compared
// (defaults to "yday"; shows e.g. "28 Oct" when the previous day was a holiday).
function LineMetricCell({ today, yesterday, mono = false, align = "left", testid, cmpLabel = "yday" }) {
  const t = Number(today) || 0;
  const y = Number(yesterday) || 0;
  const Icon = t > y ? TrendUp : t < y ? TrendDown : Minus;
  const cls = t > y ? "text-emerald-600" : t < y ? "text-rose-600" : "text-slate-400";
  return (
    <div className={align === "right" ? "flex flex-col items-end" : ""} data-testid={testid}>
      <span className={`${mono ? "font-mono" : ""} font-semibold`}>{t.toLocaleString()}</span>
      <span className={`text-[10.5px] font-semibold inline-flex items-center gap-0.5 ${cls}`}
        title={`Today: ${t.toLocaleString()} · ${cmpLabel}: ${y.toLocaleString()}`}>
        <Icon className="h-3 w-3" strokeWidth={2.5} />
        <span className="font-mono">{y.toLocaleString()}</span>
        <span className="font-normal text-slate-400">{cmpLabel}</span>
      </span>
    </div>
  );
}

function MiniCard({ label, value, icon: Icon, tone, prev, trendTestid, prevLabel }) {
  const tones = { brand: "bg-rose-50 text-brand", blue: "bg-blue-50 text-blue-700", amber: "bg-amber-50 text-amber-700", emerald: "bg-emerald-50 text-emerald-700", slate: "bg-slate-100 text-slate-700" };
  return (
    <Card className="p-4 border-slate-200 rounded-md bg-white">
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon className="h-4.5 w-4.5" /></div>
        <div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
          <div className="font-heading font-bold text-xl leading-tight">{value}</div>
          <TrendVsYesterday today={value} yesterday={prev} label={prevLabel} testid={trendTestid} />
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="mt-1 font-mono font-bold text-[15px] sm:text-lg">{Number(value).toLocaleString()}</div>
    </div>
  );
}

function SOListTable({ orders, lines, user, canTickActive, canUntickActive, canEditActivelyRunning, canEditTarget, canAssignLines, canDeleteOutputRow, openOutputRows, toggleActive, toggleActivelyRunning, saveDailyTarget, saveLineDailyTargets, toggleAssignedLine, loadAll }) {
  // Split SOs into "in progress" (main list) and "fully produced" (collapsed footer
  // section) — the same behaviour the Dashboard already uses. An SO is considered
  // complete when `produced_qty >= order_qty` (and order_qty > 0), but it only
  // moves here after the next 12 AM IST following its completion day.
  const { openOrders, completedOrders } = useMemo(() => {
    const open = [], done = [];
    for (const o of (orders || [])) {
      if (isCompletedForDisplay(o)) done.push(o); else open.push(o);
    }
    return { openOrders: open, completedOrders: done };
  }, [orders]);
  const [showCompleted, setShowCompleted] = useState(false);
  const columns = useMemo(() => ({
    active:     { get: (o) => (o.active_for_production ? 1 : 0), type: "number" },
    running:    { get: (o) => (o.actively_running ? 1 : 0), type: "number" },
    order:      { get: (o) => o.order_number || "", type: "text" },
    buyer:      { get: (o) => o.buyer || "", type: "text" },
    bbs_style:  { get: (o) => o.bbs_style || o.style || "", type: "text" },
    buyer_style:{ get: (o) => o.buyer_style || "", type: "text" },
    order_qty:  { get: (o) => Number(o.order_qty || 0), type: "number" },
    produced:   { get: (o) => Number(o.produced_qty || 0), type: "number" },
    target:     { get: (o) => Number(o.daily_target || 0), type: "number" },
    delivery:   { get: (o) => o.delivery_date || "", type: "date" },
  }), []);
  const { sortedRows, sortKey, sortDir, cycleSort } = useSortableTable(openOrders, columns, { storageKey: "sort:opsiq:so-list" });
  return (
    <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-semibold text-[15px]">Sales Order List</div>
          <div className="text-[12px] text-slate-500">
            Synced from the Order Google Sheet (admins manage the sync under Masters → Order Sheet). Auto-refreshes every 15 minutes.
            {" "}Line Supervisors only see SOs marked <b>Active</b> in the Output Entry dropdown.
          </div>
        </div>
        {(user?.role === "admin" || user?.role === "admin_addl") && (
          <div className="flex items-center gap-2 flex-wrap">
            <CleanseMismatchedOutputButton onDone={loadAll} />
            <BackfillButton orders={orders} lines={lines} onDone={loadAll} />
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="opsiq-table">
          <thead>
            <tr>
              <SortableTh label="Active" colKey="active" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} className="w-[130px]" />
              <SortableTh label="Actively Running" colKey="running" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} className="w-[130px]" />
              <SortableTh label="SO #" colKey="order" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} />
              <SortableTh label="Buyer" colKey="buyer" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} />
              <SortableTh label="BBS Style" colKey="bbs_style" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} />
              <SortableTh label="Buyer Style" colKey="buyer_style" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} />
              <SortableTh label="Ord Qty" colKey="order_qty" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} align="right" />
              <SortableTh label="Produced" colKey="produced" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} align="right" />
              <SortableTh label="Daily Target" colKey="target" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} className="w-[160px]" />
              <th style={{ width: 200 }}>Assigned Lines</th>
              <SortableTh label="Delivery" colKey="delivery" sortKey={sortKey} sortDir={sortDir} onClick={cycleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((o) => {
              const active = !!o.active_for_production;
              const disabled = active ? !canUntickActive : !canTickActive;
              const runningNow = !!o.actively_running;
              return (
                <tr key={o.order_number}>
                  <td>
                    <label className={`inline-flex items-center gap-2 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`} data-testid={`so-active-${o.order_number}`}>
                      <Checkbox checked={active} disabled={disabled} onCheckedChange={() => !disabled && toggleActive(o)} />
                      <span className={`text-[11px] font-semibold uppercase tracking-wide ${active ? "text-emerald-700" : "text-slate-400"}`}>{active ? "Active" : "Inactive"}</span>
                    </label>
                  </td>
                  <td>
                    <label
                      className={`inline-flex items-center gap-2 ${!canEditActivelyRunning ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`so-actively-running-${o.order_number}`}
                      title="Manually flag this SO as in-production so target-shortfall alerts kick in from day one (before any output has been recorded)."
                    >
                      <Checkbox checked={runningNow} disabled={!canEditActivelyRunning} onCheckedChange={() => canEditActivelyRunning && toggleActivelyRunning(o)} />
                      <span className={`text-[11px] font-semibold uppercase tracking-wide ${runningNow ? "text-indigo-700" : "text-slate-400"}`}>{runningNow ? "Running" : "Off"}</span>
                    </label>
                  </td>
                  <td className="font-mono font-semibold text-brand">{o.order_number}</td>
                  <td>{o.buyer}</td>
                  <td className="text-slate-700 font-mono text-[12.5px]">{o.bbs_style || o.style}</td>
                  <td className="text-slate-500 font-mono text-[12.5px]">{o.buyer_style || "—"}</td>
                  <td className="text-right font-mono">{Number(o.order_qty || 0).toLocaleString()}</td>
                  <td className="text-right font-mono">
                    <div className="inline-flex items-center gap-1.5 justify-end">
                      <span>{Number(o.produced_qty || 0).toLocaleString()}</span>
                      {canDeleteOutputRow && (
                        <button
                          type="button"
                          onClick={() => openOutputRows(o)}
                          title="View or delete individual output rows for this SO (admin only)"
                          className="text-slate-400 hover:text-rose-600 transition-colors"
                          data-testid={`view-output-rows-${o.order_number}`}
                        >
                          <ListTree className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <DailyTargetCell order={o} lines={lines} canEdit={canEditTarget} onSave={(v) => saveDailyTarget(o, v)} onSaveLineTargets={(map) => saveLineDailyTargets(o, map)} />
                  </td>
                  <td>
                    <AssignedLinesCell order={o} lines={lines} canEdit={canAssignLines} onToggle={(lineId) => toggleAssignedLine(o, lineId)} />
                  </td>
                  <td className="font-mono text-slate-600 text-[12.5px]">{o.delivery_date || "—"}</td>
                </tr>
              );
            })}
            {sortedRows.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-slate-500">{completedOrders.length > 0 ? "All synced SOs are fully produced — see completed section below." : "No orders synced yet."}</td></tr>}
          </tbody>
        </table>
      </div>
      {completedOrders.length > 0 && (
        <div className="border-t border-slate-200 bg-emerald-50/40" data-testid="so-list-completed-section">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-emerald-100/40 transition-colors"
            data-testid="so-list-completed-toggle"
          >
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10.5px] font-bold">{completedOrders.length}</span>
              <span className="text-[13px] font-semibold text-emerald-800">Completed SOs</span>
              <span className="text-[11.5px] text-emerald-700/80">— fully produced, hidden by default. Click to {showCompleted ? "collapse" : "expand"}.</span>
            </div>
            <span className="text-[11px] font-mono text-emerald-700">{showCompleted ? "▲" : "▼"}</span>
          </button>
          {showCompleted && (
            <div className="overflow-x-auto border-t border-emerald-200">
              <table className="opsiq-table">
                <thead>
                  <tr>
                    <th>SO #</th>
                    <th>Buyer</th>
                    <th>BBS Style</th>
                    <th>Buyer Style</th>
                    <th className="text-right">Ord Qty</th>
                    <th className="text-right">Produced</th>
                    <th>Delivery</th>
                    <th style={{ width: 200 }}>Assigned Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {completedOrders
                    .slice()
                    .sort((a, b) => (b.produced_qty || 0) - (a.produced_qty || 0))
                    .map((o) => (
                      <tr key={o.order_number} data-testid={`so-completed-row-${o.order_number}`} className="bg-white/70">
                        <td className="font-mono font-semibold text-slate-700">{o.order_number}</td>
                        <td>{o.buyer || "—"}</td>
                        <td className="font-mono text-[12.5px]">{o.bbs_style || o.style || "—"}</td>
                        <td className="font-mono text-[12.5px]">{o.buyer_style || "—"}</td>
                        <td className="text-right font-mono">{Number(o.order_qty || 0).toLocaleString()}</td>
                        <td className="text-right font-mono text-emerald-700 font-semibold">{Number(o.produced_qty || 0).toLocaleString()}</td>
                        <td className="font-mono text-slate-600 text-[12.5px]">{o.delivery_date || "—"}</td>
                        <td>
                          <AssignedLinesCell order={o} lines={lines} canEdit={canAssignLines} onToggle={(lineId) => toggleAssignedLine(o, lineId)} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function DailyTargetCell({ order, lines = [], canEdit, onSave, onSaveLineTargets }) {  const [val, setVal] = useState(order.daily_target ?? "");
  const [showLineTargets, setShowLineTargets] = useState(false);
  const assigned = order.assigned_lines || [];
  const isMultiLine = assigned.length > 1;
  const overrides = order.line_daily_targets || {};
  const lineMap = Object.fromEntries((lines || []).map((l) => [l.id, l.name]));
  const equalSplit = (Number(order.daily_target) > 0 && assigned.length > 0)
    ? Math.round(Number(order.daily_target) / assigned.length)
    : 0;
  const [draftMap, setDraftMap] = useState(() => {
    const seed = {};
    for (const lid of assigned) seed[lid] = overrides[lid] ? String(overrides[lid]) : "";
    return seed;
  });
  useEffect(() => { setVal(order.daily_target ?? ""); }, [order.daily_target]);
  useEffect(() => {
    // Reseed the draft map when assigned lines or overrides change externally
    const seed = {};
    for (const lid of assigned) seed[lid] = overrides[lid] ? String(overrides[lid]) : "";
    setDraftMap(seed);
    // eslint-disable-next-line
  }, [assigned.join(","), JSON.stringify(overrides)]);
  const hasAnyOverride = Object.keys(overrides).length > 0;
  if (!canEdit) {
    return (
      <div className="font-mono text-slate-600 text-[13px]">
        {order.daily_target ? Number(order.daily_target).toLocaleString() : "—"}
        {hasAnyOverride && <div className="text-[10px] text-indigo-600 mt-0.5">per-line overrides set</div>}
      </div>
    );
  }
  const saveLineTargets = () => {
    // Build clean map: keep only positive integer values
    const map = {};
    for (const lid of assigned) {
      const raw = String(draftMap[lid] ?? "").trim();
      if (raw === "") continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) continue;
      map[lid] = Math.round(n);
    }
    onSaveLineTargets(map);
    setShowLineTargets(false);
  };
  const resetAllOverrides = () => {
    onSaveLineTargets({});
    setShowLineTargets(false);
  };
  return (
    <div className="relative flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Input
          inputMode="numeric"
          pattern="[0-9]*"
          type="number"
          min={0}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => {
            const same = (val === "" && (order.daily_target === null || order.daily_target === undefined)) || String(order.daily_target ?? "") === String(val);
            if (!same) onSave(val);
          }}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          placeholder="—"
          className="h-9 w-24 text-right font-mono"
          data-testid={`daily-target-${order.order_number}`}
        />
        {isMultiLine && Number(order.daily_target) > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowLineTargets((v) => !v)}
            className={`h-9 px-2 text-[10.5px] font-semibold ${hasAnyOverride ? "border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100" : "text-slate-600"}`}
            title="Set per-line target overrides for this multi-line SO"
            data-testid={`line-targets-btn-${order.order_number}`}
          >
            {hasAnyOverride ? "Per-line ✓" : "Per-line"}
          </Button>
        )}
      </div>
      {showLineTargets && (
        <div className="absolute z-20 mt-1 bg-white border border-indigo-300 rounded-md shadow-lg p-3 w-72" data-testid={`line-targets-popover-${order.order_number}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-800 mb-1">Per-line daily target</div>
          <div className="text-[10.5px] text-slate-500 mb-2 leading-relaxed">
            Leave blank on a line to use the equal split of <span className="font-mono">{equalSplit.toLocaleString()}</span> pcs. Only positive numbers save.
          </div>
          <div className="space-y-1.5">
            {assigned.map((lid) => (
              <div key={lid} className="flex items-center gap-2">
                <div className="text-[12px] font-medium text-slate-700 w-24 truncate" title={lineMap[lid] || lid}>{lineMap[lid] || lid}</div>
                <Input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={0}
                  value={draftMap[lid] ?? ""}
                  placeholder={`= ${equalSplit}`}
                  onChange={(e) => setDraftMap((prev) => ({ ...prev, [lid]: e.target.value }))}
                  className="h-8 text-right font-mono"
                  data-testid={`line-target-input-${order.order_number}-${lid}`}
                />
                <span className="text-[10.5px] text-slate-400">pcs</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-slate-200">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={resetAllOverrides}
              className="text-[11px] text-slate-500 h-7"
              data-testid={`line-targets-reset-${order.order_number}`}
              disabled={!hasAnyOverride}
            >
              Reset all
            </Button>
            <div className="flex items-center gap-1.5">
              <Button type="button" size="sm" variant="outline" onClick={() => setShowLineTargets(false)} className="h-7 text-[11px]">Cancel</Button>
              <Button
                type="button"
                size="sm"
                onClick={saveLineTargets}
                className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid={`line-targets-save-${order.order_number}`}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
      {hasAnyOverride && !showLineTargets && (
        <div className="text-[10px] text-indigo-600 font-mono truncate max-w-[180px]">
          {Object.entries(overrides).map(([lid, v]) => `${lineMap[lid] || lid}:${v}`).join(", ")}
        </div>
      )}
    </div>
  );
}


function OrderQtyCell({ order, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(order.order_qty ?? "");
  useEffect(() => { setVal(order.order_qty ?? ""); }, [order.order_qty]);
  if (!canEdit) return <>{Number(order.order_qty || 0).toLocaleString()}</>;
  if (!editing) {
    // Read-only by default — the pencil unlocks editing so qty can't change by accident.
    return (
      <span className="inline-flex items-center justify-end gap-1.5 w-full">
        <span className="font-mono">{Number(order.order_qty || 0).toLocaleString()}</span>
        <button
          className="text-slate-300 hover:text-brand transition-colors"
          title="Edit order qty (Admin)"
          data-testid={`order-qty-editbtn-${order.order_number}`}
          onClick={(e) => { e.stopPropagation(); setVal(order.order_qty ?? ""); setEditing(true); }}
        >
          <Pencil className="h-3 w-3" />
        </button>
      </span>
    );
  }
  const cancel = () => { setEditing(false); setVal(order.order_qty ?? ""); };
  const commit = () => {
    const changed = String(val).trim() !== String(order.order_qty ?? "");
    setEditing(false);
    if (changed) onSave(val);
    else setVal(order.order_qty ?? "");
  };
  return (
    <Input
      autoFocus
      type="number"
      min={1}
      inputMode="numeric"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") cancel();
      }}
      onClick={(e) => e.stopPropagation()}
      className="h-8 w-24 text-right font-mono ml-auto"
      title="Enter to save · Esc to cancel"
      data-testid={`order-qty-${order.order_number}`}
    />
  );
}

function ProdTable({ rows, lines, emptyLabel, enableDrillDown = true, canEditOrderQty = false, onSaveOrderQty }) {
  const lineMap = Object.fromEntries((lines || []).map((l) => [l.id, l.name]));
  const [expanded, setExpanded] = useState({});
  const [detail, setDetail] = useState({}); // { [orderNumber]: { loading, data, error } }
  const toggleExpand = async (orderNumber) => {
    const next = !expanded[orderNumber];
    setExpanded((prev) => ({ ...prev, [orderNumber]: next }));
    if (next && !detail[orderNumber]) {
      setDetail((prev) => ({ ...prev, [orderNumber]: { loading: true } }));
      try {
        const { data } = await api.get(`/orders/${encodeURIComponent(orderNumber)}/line-breakdown`, { params: { days: 30 } });
        setDetail((prev) => ({ ...prev, [orderNumber]: { loading: false, data } }));
      } catch (e) {
        setDetail((prev) => ({ ...prev, [orderNumber]: { loading: false, error: e.response?.data?.detail || "Failed to load" } }));
      }
    }
  };
  return (
    <div className="overflow-x-auto">
      <table className="opsiq-table compact">
        <thead>
          <tr>
            {enableDrillDown && <th className="w-6"></th>}
            <th>SO #</th><th>Buyer</th><th>BBS Style</th><th>Buyer Style</th><th>Assigned Lines</th>
            <th className="text-right">Ord Qty</th><th className="text-right">Produced</th>
            <th className="text-right">Balance</th><th className="text-right">Today</th>
            <th className="text-right">Target</th><th className="text-right">Achieved %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const pct = o.order_qty > 0 ? Math.min(100, Math.round((o.produced_qty / o.order_qty) * 100)) : 0;
            const target = Number(o.daily_target || 0);
            const achievedPct = target > 0 ? Math.round((Number(o.today_output || 0) / target) * 100) : null;
            const achievedTone = achievedPct === null ? "text-slate-400" :
              achievedPct >= 100 ? "bg-emerald-100 text-emerald-800" :
              achievedPct >= 70 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
            const assigned = (o.assigned_lines || []).map((id) => lineMap[id] || "?");
            const isMultiLine = (o.assigned_lines || []).length > 1;
            const isOpen = !!expanded[o.order_number];
            const canDrill = enableDrillDown && (o.assigned_lines || []).length > 0;
            const rowDetail = detail[o.order_number];
            return (
              <Fragment key={o.order_number}>
                <tr className={canDrill ? "cursor-pointer hover:bg-slate-50" : ""} onClick={canDrill ? () => toggleExpand(o.order_number) : undefined} data-testid={`prod-row-${o.order_number}`}>
                  {enableDrillDown && (
                    <td className="text-slate-400">
                      {canDrill ? (isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : ""}
                    </td>
                  )}
                  <td className="font-mono font-semibold text-brand">
                    {o.order_number}
                    {isMultiLine && <span className="ml-1.5 text-[10px] uppercase tracking-wider font-mono px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">multi-line</span>}
                  </td>
                  <td>{o.buyer}</td>
                  <td className="text-slate-700 font-mono text-[12.5px]">{o.bbs_style || o.style}</td>
                  <td className="text-slate-600 font-mono text-[12.5px]" data-testid={`prod-buyerstyle-${o.order_number}`}>{o.buyer_style || "—"}</td>
                  <td className="text-[11.5px]">
                    {assigned.length === 0 ? <span className="text-slate-400">Unassigned</span> :
                      <div className="flex flex-wrap gap-1">
                        {assigned.map((name, i) => <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono text-[10.5px]">{name}</span>)}
                      </div>}
                  </td>
                  <td className="text-right font-mono" data-testid={`prod-orderqty-cell-${o.order_number}`}>
                    <OrderQtyCell order={o} canEdit={canEditOrderQty} onSave={(v) => onSaveOrderQty && onSaveOrderQty(o, v)} />
                  </td>
                  <td className="text-right font-mono">
                    <div>{o.produced_qty.toLocaleString()}</div>
                    <div className="mt-1 h-1 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-brand" style={{ width: `${pct}%` }} /></div>
                    <div className="text-[10.5px] text-slate-500 mt-0.5">{pct}%</div>
                  </td>
                  <td className="text-right font-mono font-semibold">{o.balance.toLocaleString()}</td>
                  <td className="text-right font-mono">{o.today_output.toLocaleString()}</td>
                  <td className="text-right font-mono text-slate-600">{target > 0 ? target.toLocaleString() : "—"}</td>
                  <td className="text-right">
                    {achievedPct === null ? <span className="font-mono text-slate-400">—</span> :
                      <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[11.5px] font-mono font-semibold ${achievedTone}`}>{achievedPct}%</span>}
                  </td>
                </tr>
                {enableDrillDown && isOpen && (
                  <tr className="bg-slate-50/60" data-testid={`prod-drill-${o.order_number}`}>
                    <td colSpan={12} className="p-3">
                      {rowDetail?.loading && <div className="text-[12.5px] text-slate-500 py-3 text-center">Loading line breakdown…</div>}
                      {rowDetail?.error && <div className="text-[12.5px] text-red-600 py-3 text-center">{rowDetail.error}</div>}
                      {rowDetail?.data && <LineBreakdown data={rowDetail.data} />}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={enableDrillDown ? 12 : 11} className="text-center py-8 text-slate-500 text-[12.5px]">{emptyLabel}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function LineBreakdown({ data }) {
  const trendData = useMemo(() => {
    const dates = data?.trend?.dates || [];
    return dates.map((d, i) => {
      const row = { date: d.slice(5) };
      (data.trend.series || []).forEach((s) => { row[s.line_name] = s.values[i] || 0; });
      return row;
    });
  }, [data]);
  const palette = ["#7C1D3A", "#1D4ED8", "#059669", "#D97706", "#7C3AED", "#DC2626", "#0891B2"];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_1fr] gap-4">
      <div>
        <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-600 mb-1.5">Per-line output</div>
        <table className="opsiq-table">
          <thead><tr><th>Line</th><th className="text-right">Produced</th><th className="text-right">Today</th><th className="text-right">Target</th><th className="text-right">Achieved</th></tr></thead>
          <tbody>
            {data.lines.map((l) => (
              <tr key={l.line_id}>
                <td className="font-semibold">{l.line_name}</td>
                <td className="text-right font-mono">{Number(l.produced || 0).toLocaleString()}</td>
                <td className="text-right font-mono">{Number(l.today || 0).toLocaleString()}</td>
                <td className="text-right font-mono text-slate-600">{l.target ? l.target.toLocaleString() : "—"}</td>
                <td className="text-right">
                  {l.achieved_pct === null || l.achieved_pct === undefined ? <span className="font-mono text-slate-400">—</span> :
                    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[11.5px] font-mono font-semibold ${l.achieved_pct >= 100 ? "bg-emerald-100 text-emerald-800" : l.achieved_pct >= 70 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{l.achieved_pct}%</span>}
                </td>
              </tr>
            ))}
            {data.lines.length === 0 && <tr><td colSpan={5} className="text-center py-3 text-slate-500 text-[12.5px]">No output rows yet</td></tr>}
          </tbody>
        </table>
        {data.daily_target > 0 && (data.lines.length > 1) && (
          <div className="text-[10.5px] text-slate-500 mt-1.5">Daily target of {data.daily_target.toLocaleString()} pcs is split equally across {data.lines.length} assigned lines.</div>
        )}
      </div>
      <div className="min-h-[200px]">
        <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-600 mb-1.5">Last 30 days · per-line trend</div>
        {trendData.length === 0 ? (
          <div className="text-[12.5px] text-slate-500">No trend data.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" fontSize={10} tick={{ fill: "#64748B" }} />
              <YAxis fontSize={10} tick={{ fill: "#64748B" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {(data.trend?.series || []).map((s, i) => (
                <Line key={s.line_id} type="monotone" dataKey={s.line_name} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function AssignedLinesCell({ order, lines, canEdit, onToggle }) {
  const [open, setOpen] = useState(false);
  const assigned = order.assigned_lines || [];
  const label = assigned.length === 0
    ? (canEdit ? "Assign lines…" : "Unassigned")
    : `${assigned.length} line${assigned.length === 1 ? "" : "s"}`;
  if (!canEdit) {
    return (
      <div className="text-[11.5px]">
        {assigned.length === 0 ? <span className="text-slate-400">Unassigned</span> :
          <div className="flex flex-wrap gap-1">
            {assigned.map((id) => {
              const l = lines.find((x) => x.id === id);
              return <span key={id} className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono text-[10.5px]">{l?.name || "?"}</span>;
            })}
          </div>}
      </div>
    );
  }
  return (
    <div className="relative" data-testid={`assign-lines-${order.order_number}`}>
      <Button type="button" size="sm" variant="outline" className="h-8 text-[11.5px] w-full justify-start" onClick={() => setOpen(!open)}>
        {label}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-56 rounded-md border border-slate-200 bg-white shadow-lg p-2 max-h-64 overflow-y-auto">
            {lines.map((l) => {
              const isOn = assigned.includes(l.id);
              return (
                <label key={l.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-[12.5px]">
                  <input type="checkbox" checked={isOn} onChange={() => onToggle(l.id)} className="accent-[#7C1D3A]" />
                  <span className={isOn ? "font-semibold text-slate-800" : "text-slate-600"}>{l.name}</span>
                </label>
              );
            })}
            {lines.length === 0 && <div className="text-[11.5px] text-slate-500 text-center py-2">No lines defined in Masters</div>}
          </div>
        </>
      )}
    </div>
  );
}

function VoiceNotesBrowser({ lines, canDelete }) {
  const [notes, setNotes] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(todayStr());
  const [lineFilter, setLineFilter] = useState("all");
  const [playingId, setPlayingId] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (lineFilter !== "all") params.line_id = lineFilter;
      const { data } = await api.get("/supervisor-notes", { params });
      setNotes(data);
    } catch { setNotes([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, lineFilter]);

  const play = async (n) => {
    if (playingId === n.id) { audioRef.current?.pause(); setPlayingId(null); return; }
    try {
      let url = blobUrls[n.id];
      if (!url) {
        const { data } = await api.get(`/supervisor-notes/${n.id}/audio`, { responseType: "blob" });
        url = URL.createObjectURL(data);
        setBlobUrls((p) => ({ ...p, [n.id]: url }));
      }
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.onended = () => setPlayingId(null);
      await audioRef.current.play();
      setPlayingId(n.id);
    } catch { toast.error("Couldn't play audio"); }
  };
  const del = async (n) => {
    if (!window.confirm("Delete this voice note?")) return;
    try { await api.delete(`/supervisor-notes/${n.id}`); setNotes((p) => p.filter((x) => x.id !== n.id)); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const lineMap = Object.fromEntries(lines.map((l) => [l.id, l.name]));

  return (
    <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-semibold text-[15px]">Supervisor Voice Notes</div>
          <div className="text-[12px] text-slate-500">All voice notes recorded by line supervisors across every line.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40 text-[12.5px]" data-testid="voice-filter-from" />
          <span className="text-slate-400 text-[12.5px]">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40 text-[12.5px]" data-testid="voice-filter-to" />
          <Select value={lineFilter} onValueChange={setLineFilter}>
            <SelectTrigger className="h-9 w-40 text-[12.5px]" data-testid="voice-filter-line"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lines</SelectItem>
              {lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {notes.length === 0 && <div className="p-8 text-center text-slate-500 text-[13px]">No voice notes in this range.</div>}
        {notes.map((n) => (
          <div key={n.id} className="p-3 flex items-center gap-3" data-testid={`voice-browser-${n.id}`}>
            <Button size="sm" variant="outline" className="h-9 w-9 p-0 shrink-0" onClick={() => play(n)}>
              {playingId === n.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-slate-800 truncate">
                {n.recorded_by} <span className="text-slate-400 font-normal">· {n.recorded_by_role}</span>
              </div>
              <div className="text-[11px] font-mono text-slate-500 flex items-center gap-2 flex-wrap">
                <span>{n.date}</span>
                <span>·</span>
                <span className="text-blue-700">{lineMap[n.line_id] || n.line_id}</span>
                {n.order_number && <><span>·</span><span className="text-brand">{n.order_number}</span></>}
                {n.duration_ms > 0 && <><span>·</span><span>{Math.round(n.duration_ms/1000)}s</span></>}
                <span>·</span>
                <span className="text-slate-400">{new Date(n.created_at).toLocaleTimeString()}</span>
              </div>
            </div>
            {canDelete && (
              <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-slate-400 hover:text-red-600" onClick={() => del(n)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}


/* -------------------- History Panel -------------------- */

function _fmtDate(d) { return d.toISOString().slice(0, 10); }
function _startOfWeek(d) { const dt = new Date(d); const day = dt.getDay(); const diff = (day === 0 ? -6 : 1 - day); dt.setDate(dt.getDate() + diff); return dt; }
function _startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function _startOfLastMonth(d) { return new Date(d.getFullYear(), d.getMonth() - 1, 1); }
function _endOfLastMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 0); }

function HistoryPanel({ lines }) {
  const today = new Date();
  const [subTab, setSubTab] = useState("output");
  const [dateFrom, setDateFrom] = useState(() => _fmtDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)));
  const [dateTo, setDateTo] = useState(() => _fmtDate(today));
  const [lineFilter, setLineFilter] = useState("all");
  const [soFilter, setSoFilter] = useState("");
  const [attendance, setAttendance] = useState([]);
  const [output, setOutput] = useState([]);
  const [loading, setLoading] = useState(false);
  const lineMap = useMemo(() => Object.fromEntries((lines || []).map((l) => [l.id, l.name])), [lines]);

  const attColumns = useMemo(() => ({
    date:       { get: (r) => r.date || "", type: "date" },
    line:       { get: (r) => lineMap[r.line_id] || r.line_id || "", type: "text" },
    stitchers:  { get: (r) => Number(r.stitchers || 0), type: "number" },
    helpers:    { get: (r) => Number(r.helpers || 0), type: "number" },
    qc:         { get: (r) => Number(r.qc || 0), type: "number" },
    total:      { get: (r) => Number(r.stitchers || 0) + Number(r.helpers || 0) + Number(r.qc || 0), type: "number" },
    updated_by: { get: (r) => r.updated_by || "", type: "text" },
  }), [lineMap]);
  const outColumns = useMemo(() => ({
    date:       { get: (r) => r.date || "", type: "date" },
    line:       { get: (r) => lineMap[r.line_id] || r.line_id || "", type: "text" },
    order:      { get: (r) => r.order_number || "", type: "text" },
    buyer:      { get: (r) => r.buyer || "", type: "text" },
    style:      { get: (r) => r.style || "", type: "text" },
    qty:        { get: (r) => Number(r.qty || 0), type: "number" },
    updated_by: { get: (r) => r.updated_by || "", type: "text" },
  }), [lineMap]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, o] = await Promise.all([
        api.get("/attendance", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/output", { params: { date_from: dateFrom, date_to: dateTo } }),
      ]);
      setAttendance(a.data || []);
      setOutput(o.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load history"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const applyPreset = (which) => {
    const now = new Date();
    if (which === "this-week") {
      setDateFrom(_fmtDate(_startOfWeek(now)));
      setDateTo(_fmtDate(now));
    } else if (which === "this-month") {
      setDateFrom(_fmtDate(_startOfMonth(now)));
      setDateTo(_fmtDate(now));
    } else if (which === "last-month") {
      setDateFrom(_fmtDate(_startOfLastMonth(now)));
      setDateTo(_fmtDate(_endOfLastMonth(now)));
    } else if (which === "last-30") {
      setDateFrom(_fmtDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29)));
      setDateTo(_fmtDate(now));
    }
  };

  const attendanceFiltered = useMemo(() => {
    let rows = attendance.slice();
    if (lineFilter !== "all") rows = rows.filter((r) => r.line_id === lineFilter);
    return rows;
  }, [attendance, lineFilter]);
  const outputFiltered = useMemo(() => {
    let rows = output.slice();
    if (lineFilter !== "all") rows = rows.filter((r) => r.line_id === lineFilter);
    if (soFilter.trim()) {
      const q = soFilter.trim().toLowerCase();
      rows = rows.filter((r) => (r.order_number || "").toLowerCase().includes(q));
    }
    return rows;
  }, [output, lineFilter, soFilter]);
  const { sortedRows: attendanceRows, sortKey: attSortKey, sortDir: attSortDir, cycleSort: attCycle } = useSortableTable(attendanceFiltered, attColumns, { defaultKey: "date", defaultDir: "desc", storageKey: "sort:opsiq:hist-att" });
  const { sortedRows: outputRows, sortKey: outSortKey, sortDir: outSortDir, cycleSort: outCycle } = useSortableTable(outputFiltered, outColumns, { defaultKey: "date", defaultDir: "desc", storageKey: "sort:opsiq:hist-out" });

  const attSummary = useMemo(() => {
    const totalStitchers = attendanceRows.reduce((s, r) => s + Number(r.stitchers || 0), 0);
    const totalHelpers = attendanceRows.reduce((s, r) => s + Number(r.helpers || 0), 0);
    const totalQc = attendanceRows.reduce((s, r) => s + Number(r.qc || 0), 0);
    return { totalStitchers, totalHelpers, totalQc, total: totalStitchers + totalHelpers + totalQc, rows: attendanceRows.length };
  }, [attendanceRows]);

  const outSummary = useMemo(() => {
    const totalQty = outputRows.reduce((s, r) => s + Number(r.qty || 0), 0);
    return { totalQty, rows: outputRows.length };
  }, [outputRows]);

  const suffix = `${dateFrom}_to_${dateTo}`;

  const exportAttendanceCSV = () => {
    const headers = ["Date", "Line", "Stitchers", "Helpers", "QC", "Total", "Updated By"];
    const rows = attendanceRows.map((r) => [
      r.date, lineMap[r.line_id] || r.line_id,
      r.stitchers || 0, r.helpers || 0, r.qc || 0,
      (Number(r.stitchers || 0) + Number(r.helpers || 0) + Number(r.qc || 0)),
      r.updated_by || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `attendance_${suffix}.csv`; a.click();
    toast.success("Attendance CSV exported");
  };

  const exportAttendanceXLSX = () => {
    const rows = attendanceRows.map((r) => ({
      Date: r.date,
      Line: lineMap[r.line_id] || r.line_id,
      Stitchers: Number(r.stitchers || 0),
      Helpers: Number(r.helpers || 0),
      QC: Number(r.qc || 0),
      Total: Number(r.stitchers || 0) + Number(r.helpers || 0) + Number(r.qc || 0),
      "Updated By": r.updated_by || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, `attendance_${suffix}.xlsx`);
    toast.success("Attendance Excel exported");
  };

  const exportOutputCSV = () => {
    const headers = ["Date", "Line", "SO Number", "Buyer", "BBS Style", "Qty", "Updated By"];
    const rows = outputRows.map((r) => [
      r.date, lineMap[r.line_id] || r.line_id,
      r.order_number || "", r.buyer || "", r.style || "",
      Number(r.qty || 0), r.updated_by || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `output_${suffix}.csv`; a.click();
    toast.success("Output CSV exported");
  };

  const exportOutputXLSX = () => {
    const rows = outputRows.map((r) => ({
      Date: r.date,
      Line: lineMap[r.line_id] || r.line_id,
      "SO Number": r.order_number || "",
      Buyer: r.buyer || "",
      "BBS Style": r.style || "",
      Qty: Number(r.qty || 0),
      "Updated By": r.updated_by || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Output");
    XLSX.writeFile(wb, `output_${suffix}.xlsx`);
    toast.success("Output Excel exported");
  };

  return (
    <Card className="border-slate-200 rounded-md bg-white overflow-hidden" data-testid="history-panel">
      <div className="p-4 border-b border-slate-100 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="font-heading font-semibold text-[15px]">Historical Line Tracking</div>
            <div className="text-[12px] text-slate-500 mt-0.5">Attendance and output records across any date range.</div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="history-presets">
            <Button size="sm" variant="outline" onClick={() => applyPreset("last-30")} className="h-8 text-[11px]" data-testid="preset-last-30">Last 30 days</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("this-week")} className="h-8 text-[11px]" data-testid="preset-this-week">This week</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("this-month")} className="h-8 text-[11px]" data-testid="preset-this-month">This month</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("last-month")} className="h-8 text-[11px]" data-testid="preset-last-month">Last month</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-[13px]" data-testid="history-date-from" />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-[13px]" data-testid="history-date-to" />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">Line</label>
            <Select value={lineFilter} onValueChange={setLineFilter}>
              <SelectTrigger className="h-9 text-[13px]" data-testid="history-line-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All lines</SelectItem>
                {(lines || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1 block">SO filter (output only)</label>
            <Input type="text" placeholder="Search SO #" value={soFilter} onChange={(e) => setSoFilter(e.target.value)} className="h-9 text-[13px]" data-testid="history-so-filter" />
          </div>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <div className="px-4 pt-3 border-b border-slate-100">
          <TabsList data-testid="history-subtabs">
            <TabsTrigger value="output" data-testid="history-tab-output">Output History</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="history-tab-attendance">Attendance History</TabsTrigger>
          </TabsList>
        </div>

        {/* Output History */}
        <TabsContent value="output" className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[12.5px] text-slate-600">
              <span className="font-semibold text-slate-800">{outSummary.rows.toLocaleString()}</span> row(s) · Total <span className="font-mono font-semibold text-slate-800">{outSummary.totalQty.toLocaleString()}</span> pcs
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={exportOutputCSV} disabled={!outputRows.length} data-testid="export-output-csv" className="h-8 text-[11px]"><FileText className="h-3.5 w-3.5 mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" onClick={exportOutputXLSX} disabled={!outputRows.length} data-testid="export-output-xlsx" className="h-8 text-[11px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="opsiq-table">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <SortableTh label="Date" colKey="date" sortKey={outSortKey} sortDir={outSortDir} onClick={outCycle} />
                  <SortableTh label="Line" colKey="line" sortKey={outSortKey} sortDir={outSortDir} onClick={outCycle} />
                  <SortableTh label="SO #" colKey="order" sortKey={outSortKey} sortDir={outSortDir} onClick={outCycle} />
                  <SortableTh label="Buyer" colKey="buyer" sortKey={outSortKey} sortDir={outSortDir} onClick={outCycle} />
                  <SortableTh label="BBS Style" colKey="style" sortKey={outSortKey} sortDir={outSortDir} onClick={outCycle} />
                  <SortableTh label="Qty" colKey="qty" sortKey={outSortKey} sortDir={outSortDir} onClick={outCycle} align="right" />
                  <SortableTh label="Updated By" colKey="updated_by" sortKey={outSortKey} sortDir={outSortDir} onClick={outCycle} />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="text-center py-10 text-slate-500">Loading…</td></tr>}
                {!loading && outputRows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No output records in this range.</td></tr>}
                {!loading && outputRows.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono text-slate-700 text-[12.5px]">{r.date}</td>
                    <td className="font-semibold">{lineMap[r.line_id] || r.line_id}</td>
                    <td className="font-mono text-brand">{r.order_number}</td>
                    <td>{r.buyer}</td>
                    <td className="text-slate-600">{r.style}</td>
                    <td className="text-right font-mono font-semibold">{Number(r.qty || 0).toLocaleString()}</td>
                    <td className="text-slate-500 text-[12px] font-mono">{r.updated_by || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Attendance History */}
        <TabsContent value="attendance" className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[12.5px] text-slate-600">
              <span className="font-semibold text-slate-800">{attSummary.rows.toLocaleString()}</span> row(s) · Stitchers <span className="font-mono font-semibold">{attSummary.totalStitchers.toLocaleString()}</span> · Helpers <span className="font-mono font-semibold">{attSummary.totalHelpers.toLocaleString()}</span> · QC <span className="font-mono font-semibold">{attSummary.totalQc.toLocaleString()}</span> · Total <span className="font-mono font-semibold text-slate-800">{attSummary.total.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={exportAttendanceCSV} disabled={!attendanceRows.length} data-testid="export-attendance-csv" className="h-8 text-[11px]"><FileText className="h-3.5 w-3.5 mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" onClick={exportAttendanceXLSX} disabled={!attendanceRows.length} data-testid="export-attendance-xlsx" className="h-8 text-[11px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="opsiq-table">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <SortableTh label="Date" colKey="date" sortKey={attSortKey} sortDir={attSortDir} onClick={attCycle} />
                  <SortableTh label="Line" colKey="line" sortKey={attSortKey} sortDir={attSortDir} onClick={attCycle} />
                  <SortableTh label="Stitchers" colKey="stitchers" sortKey={attSortKey} sortDir={attSortDir} onClick={attCycle} align="right" />
                  <SortableTh label="Helpers" colKey="helpers" sortKey={attSortKey} sortDir={attSortDir} onClick={attCycle} align="right" />
                  <SortableTh label="QC" colKey="qc" sortKey={attSortKey} sortDir={attSortDir} onClick={attCycle} align="right" />
                  <SortableTh label="Total" colKey="total" sortKey={attSortKey} sortDir={attSortDir} onClick={attCycle} align="right" />
                  <SortableTh label="Updated By" colKey="updated_by" sortKey={attSortKey} sortDir={attSortDir} onClick={attCycle} />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="text-center py-10 text-slate-500">Loading…</td></tr>}
                {!loading && attendanceRows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No attendance records in this range.</td></tr>}
                {!loading && attendanceRows.map((r) => {
                  const tot = Number(r.stitchers || 0) + Number(r.helpers || 0) + Number(r.qc || 0);
                  return (
                    <tr key={r.id}>
                      <td className="font-mono text-slate-700 text-[12.5px]">{r.date}</td>
                      <td className="font-semibold">{lineMap[r.line_id] || r.line_id}</td>
                      <td className="text-right font-mono">{Number(r.stitchers || 0).toLocaleString()}</td>
                      <td className="text-right font-mono">{Number(r.helpers || 0).toLocaleString()}</td>
                      <td className="text-right font-mono">{Number(r.qc || 0).toLocaleString()}</td>
                      <td className="text-right font-mono font-semibold">{tot.toLocaleString()}</td>
                      <td className="text-slate-500 text-[12px] font-mono">{r.updated_by || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
